import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Styles from './index.styles';
import { Popover } from '../../Popover';
import { useId } from '../../../Utils/Hooks';

const {
    StyledRawInput,
    StyledLabelWrapper,
    StyledInput,
    StyledLabel,
    StyledMessage,
    StyledHelperContainer,
    StyledPopover,
} = Styles;

export const RawInput = ({
    id,
    placeholder,
    value,
    defaultValue,
    fullWidth,
    disabled,
    label,
    message,
    helperContent,
    error,
    icon,
    onChange,
    ref: wrapperRef,
    inputRef,
    children,
    isClear,
    ...otherProps
}) => {
    const [plaholderShown, setPlaceholderShown] = useState(false);
    const [open, setOpen] = useState(false);
    const ref = inputRef || useRef();
    const [rawInputId] = useId('input', id);

    const handleOnChange = evt => {
        onChange(evt);
        setPlaceholderShown(!!evt.target.value);
    };

    useEffect(() => {
        if (ref.current) {
            setPlaceholderShown(!!ref.current.value);
        }
    }, [ref, value]);

    const commonProps = {
        disabled,
        error,
        placeholderShown: !plaholderShown,
    };

    return (
        <StyledRawInput isClear={isClear} ref={wrapperRef} fullWidth={fullWidth} icon={!!icon} {...commonProps}>
            <StyledLabelWrapper>
                <StyledInput
                    isClear={isClear}
                    ref={ref}
                    id={rawInputId}
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={handleOnChange}
                    autoComplete="off"
                    defaultValue={defaultValue}
                    {...commonProps}
                    {...otherProps}
                />
                {label ? (
                    <StyledLabel htmlFor={rawInputId} {...commonProps}>
                        {label}
                        {helperContent && (
                            <StyledHelperContainer
                                id={`input-helper-button${id}`}
                                onClick={() => setOpen(prev => !prev)}
                            >
                                <FontAwesomeIcon icon={['fas', 'info']} />
                            </StyledHelperContainer>
                        )}
                    </StyledLabel>
                ) : null}
                {icon}
                {children}
                {helperContent && (
                    <Popover
                        bindTo={`input-helper-button${id}`}
                        open={!disabled && open}
                        onClose={() => setOpen(false)}
                        enableMobile
                        darkMode
                    >
                        <StyledPopover>{helperContent}</StyledPopover>
                    </Popover>
                )}
            </StyledLabelWrapper>
            {message ? <StyledMessage>{message}</StyledMessage> : null}
        </StyledRawInput>
    );
};

RawInput.propTypes = {
    id: PropTypes.string,
    placeholder: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fullWidth: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    label: PropTypes.node,
    message: PropTypes.node,
    helperContent: PropTypes.node,
    icon: PropTypes.node,
    onChange: PropTypes.func,
    ref: PropTypes.shape({ current: { value: PropTypes.string } }),
    inputRef: PropTypes.shape({}),
    children: PropTypes.node,
    isClear: PropTypes.bool,
};

/* istanbul ignore next */
RawInput.defaultProps = {
    id: '',
    defaultValue: undefined,
    value: undefined,
    placeholder: '',
    fullWidth: false,
    disabled: false,
    error: false,
    label: undefined,
    message: undefined,
    helperContent: undefined,
    icon: undefined,
    onChange: () => undefined,
    ref: null,
    inputRef: null,
    children: null,
    isClear: false,
};

RawInput.Styles = Styles;
